import { apiUrl, authHeaders } from './auth';

export const fetchMedicalProfessionals = async ({
  page = 1,
  name = '',
  status = '',
  verificationStatus = '',
}) => {
  const headers = authHeaders();
  const url =
    apiUrl +
    '/admin/medicalProfessionals' +
    `?page=${page}&name=${encodeURIComponent(name)}&status=${encodeURIComponent(status)}&verification_status=${encodeURIComponent(verificationStatus)}`;
  const resp = await fetch(url, {
    method: 'get',
    headers,
  });
  if (!resp.ok) {
    throw new Error('Error fetching medical professionals');
  }
  const json = await resp.json();
  return json;
};

export const fetchMedicalProfessional = async (id) => {
  const headers = authHeaders();
  const url = apiUrl + '/admin/medicalProfessionals/' + id;
  const resp = await fetch(url, {
    method: 'get',
    headers,
  });
  const json = await resp.json();
  return json;
};

export const updateMedicalProfessional = async (id, payload) => {
  const headers = authHeaders();
  const url = apiUrl + '/admin/medicalProfessionals/' + id;
  const resp = await fetch(url, {
    method: 'put',
    headers,
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    throw new Error('Error updating medical professional');
  }
  return await resp.json();
};

export const addMedicalProfessionalNote = async (id: number, note: string) => {
  const headers = authHeaders();
  const url = apiUrl + '/admin/medicalProfessionals/' + id + '/notes';
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ note }),
  });
  return await resp.json();
};

export const approveMedicalProfessional = async (id: number) => {
  const headers = authHeaders();
  const url = apiUrl + "/admin/approve/medicalProfessional/" + id;
  const resp = await fetch(url, {
    method: "PATCH",
    headers,
  });
  if (!resp.ok) {
    let message = "Error updating medical professional";
    try {
      const body = await resp.json();
      if (body?.error) message = body.error;
    } catch {
      // response body not JSON; keep default message
    }
    throw new Error(message);
  }
  return resp.status === 204 || resp.headers.get("content-length") === "0"
    ? null
    : await resp.json().catch(() => null);
};

interface CreateMedicalProfessionalPayload {
  person: {
    first_name: string;
    middle_name?: string;
    last_name: string;
    email?: string;
    phone?: string;
    phone_type?: string;
    address_1?: string;
    address_2?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  };
  med_center_id?: number | null;
  med_center_name?: string | null;
  location?: string | null;
  medical_professional_type_id?: number | null;
  npi_num?: string;
  force?: boolean;
}

export interface MedicalProfessionalMatch {
  id: number;
  name: string;
  email: string;
  medical_center_name: string;
  location?: string;
}

export const searchMedicalProfessionalsByName = async (
  query: string,
  signal?: AbortSignal
): Promise<MedicalProfessionalMatch[]> => {
  const headers = authHeaders();
  const url = `${apiUrl}/admin/medicalProfessionals/search?q=${encodeURIComponent(query)}`;
  const resp = await fetch(url, { method: 'GET', headers, signal });
  if (!resp.ok) throw new Error('Failed to search medical professionals');
  const body = await resp.json();
  return body?.results || [];
};

export class MedicalProfessionalDuplicateError extends Error {
  matchType: 'email' | 'name_center';
  matches: MedicalProfessionalMatch[];

  constructor(
    matchType: 'email' | 'name_center',
    matches: MedicalProfessionalMatch[]
  ) {
    super('Duplicate medical professional');
    this.matchType = matchType;
    this.matches = matches;
  }
}

export const createMedicalProfessional = async (
  payload: CreateMedicalProfessionalPayload
) => {
  const headers = authHeaders();
  const url = apiUrl + '/admin/medicalProfessionals/create';
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (resp.status === 409) {
    const body = await resp.json().catch(() => null);
    if (body?.match_type && Array.isArray(body?.matches)) {
      throw new MedicalProfessionalDuplicateError(
        body.match_type,
        body.matches
      );
    }
  }
  if (!resp.ok) {
    let message = 'Error creating medical professional';
    try {
      const body = await resp.json();
      if (body?.error) message = body.error;
    } catch {
      // response body not JSON; keep default message
    }
    throw new Error(message);
  }
  return await resp.json();
};

export const fetchMedicalProfessionalChoices = async () => {
  const headers = authHeaders();
  const url = apiUrl + '/admin/medicalProfessionalChoices';
  const resp = await fetch(url, {
    method: 'get',
    headers,
  });
  const json = await resp.json();
  return json;
};

export const fetchMedicalProfessionalPatients = async (
  id: number,
  status: string,
  page = 1
) => {
  const headers = authHeaders();
  const url = `${apiUrl}/admin/medicalProfessionals/${id}/patients?status=${status}&page=${page}`;
  const resp = await fetch(url, {
    method: 'get',
    headers,
  });
  const json = await resp.json();
  console.log('json: ', json);
  return json;
};
